import { AxiosError } from 'axios';
import { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { AppError, ErrorResponseDto } from '@/types/error';

const DEFAULT_ERROR_CODE = 'INTERNAL_ERROR';
const DEFAULT_ERROR_MESSAGE = 'Ocorreu um erro inesperado. Tente novamente.';

const FRIENDLY_ERROR_MESSAGES: Record<string, string> = {
  INTERNAL_ERROR: 'Ocorreu um erro inesperado. Tente novamente.',
  NETWORK_ERROR: 'Não foi possível conectar à API. Tente novamente.',
  UNKNOWN_ERROR: 'Ocorreu um erro inesperado. Tente novamente.',
  BULK_IMPORT_FILE_REQUIRED: 'Selecione um arquivo para continuar.',
  BULK_IMPORT_INVALID_FILE: 'Selecione um arquivo CSV válido.',
  BULK_IMPORT_INVALID_BRANDS: 'Uma ou mais marcas selecionadas não são válidas.',
  VALIDATION_ERROR: 'Verifique os dados informados e tente novamente.',
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const isErrorResponseDto = (value: unknown): value is ErrorResponseDto => {
  if (!isObject(value)) {
    return false;
  }

  return (
    typeof value.status === 'number' &&
    typeof value.code === 'string' &&
    typeof value.message === 'string' &&
    typeof value.requestId === 'string' &&
    typeof value.timestamp === 'string' &&
    typeof value.path === 'string'
  );
};

export const getFriendlyErrorMessage = (code?: string) => {
  if (!code) {
    return DEFAULT_ERROR_MESSAGE;
  }

  return FRIENDLY_ERROR_MESSAGES[code] ?? DEFAULT_ERROR_MESSAGE;
};

export const getDisplayMessage = (error: Partial<ErrorResponseDto>) => {
  if (typeof error.message === 'string' && error.message.trim().length > 0) {
    return error.message;
  }

  return getFriendlyErrorMessage(error.code);
};

const createFallbackError = (
  error: unknown,
  overrides?: Partial<ErrorResponseDto>
): AppError => {
  const timestamp = new Date().toISOString();
  const fallbackCode = overrides?.code ?? (error instanceof AxiosError ? 'NETWORK_ERROR' : DEFAULT_ERROR_CODE);

  return {
    isAppError: true,
    status: overrides?.status ?? 500,
    code: fallbackCode,
    message: getDisplayMessage({
      code: fallbackCode,
      message: overrides?.message ?? (error instanceof Error ? error.message : undefined),
    }),
    details: overrides?.details,
    requestId: overrides?.requestId ?? 'unavailable',
    timestamp,
    path: overrides?.path ?? (typeof window !== 'undefined' ? window.location.pathname : 'unknown'),
    originalError: error,
  };
};

export const normalizeApiError = (error: unknown): AppError => {
  if (isAppError(error)) {
    return error;
  }

  if (error instanceof AxiosError) {
    const responseData = error.response?.data;

    if (isErrorResponseDto(responseData)) {
      return {
        ...responseData,
        message: getDisplayMessage(responseData),
        isAppError: true,
        originalError: error,
      };
    }

    return createFallbackError(error, {
      status: error.response?.status ?? 500,
      message: typeof responseData === 'string' ? responseData : undefined,
      details: isObject(responseData) ? responseData : undefined,
      path: error.config?.url ?? undefined,
    });
  }

  return createFallbackError(error);
};

export const isAppError = (error: unknown): error is AppError =>
  isObject(error) &&
  error.isAppError === true &&
  typeof error.code === 'string' &&
  typeof error.message === 'string';

const readDetailMessage = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }

  if (Array.isArray(value)) {
    const messages = value.map(readDetailMessage).filter((item): item is string => !!item);
    return messages[0] ?? null;
  }

  if (isObject(value)) {
    if (typeof value.message === 'string' && value.message.trim().length > 0) {
      return value.message;
    }

    if (isObject(value.constraints)) {
      const constraintMessages = Object.values(value.constraints)
        .map(readDetailMessage)
        .filter((item): item is string => !!item);
      return constraintMessages[0] ?? null;
    }
  }

  return null;
};

const normalizeFieldEntries = (details: unknown): Array<{ field: string; message: string }> => {
  if (Array.isArray(details)) {
    return details.flatMap((entry) => {
      if (!isObject(entry)) {
        return [];
      }

      const field = [entry.field, entry.property, entry.path]
        .find((value): value is string => typeof value === 'string' && value.trim().length > 0);
      const message = readDetailMessage(entry.message) ?? readDetailMessage(entry.constraints) ?? readDetailMessage(entry);

      if (!field || !message) {
        return [];
      }

      return [{ field, message }];
    });
  }

  if (isObject(details)) {
    return Object.entries(details).flatMap(([field, value]) => {
      const message = readDetailMessage(value);

      if (!message) {
        return [];
      }

      return [{ field, message }];
    });
  }

  return [];
};

export const applyFormErrorDetails = <TFieldValues extends FieldValues>(
  details: unknown,
  setError: UseFormSetError<TFieldValues>
) => {
  const entries = normalizeFieldEntries(details);

  entries.forEach(({ field, message }) => {
    setError(field as Path<TFieldValues>, {
      type: 'server',
      message,
    });
  });

  return entries.length > 0;
};

export const getErrorSupportMessage = (error: unknown) => {
  const normalizedError = normalizeApiError(error);
  return normalizedError.requestId !== 'unavailable'
    ? `${normalizedError.message} Protocolo: ${normalizedError.requestId}`
    : normalizedError.message;
};
